import type { AiArticlesResponse } from '../types/news.ts'

export const ARTICLE_POLL_INTERVAL_MS = 5 * 60 * 1000
export const ARTICLE_MIN_REFRESH_INTERVAL_MS = 60 * 1000
export const ARTICLE_REQUEST_TIMEOUT_MS = 12 * 1000

export interface ArticleFreshnessEnvironment {
  windowEvents: EventTarget
  documentEvents: EventTarget
  isVisible: () => boolean
  now: () => number
  setInterval: (callback: () => void, delay: number) => number
  clearInterval: (handle: number) => void
  setTimeout: (callback: () => void, delay: number) => number
  clearTimeout: (handle: number) => void
}

export async function fetchCurrentArticles(signal: AbortSignal, fetcher: typeof fetch = fetch): Promise<AiArticlesResponse> {
  const response = await fetcher('/api/company-articles', {
    signal, cache: 'reload', headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(`Article updates returned ${response.status}`)
  const payload = await response.json() as AiArticlesResponse
  if (!payload || payload.cache !== 'fresh' || !Array.isArray(payload.articles)
    || !payload.articles.every(article => article && typeof article.id === 'string'
      && typeof article.headline === 'string' && typeof article.generatedAt === 'string'
      && Number.isFinite(Date.parse(article.generatedAt)) && Array.isArray(article.sources)
      && Array.isArray(article.sections) && Array.isArray(article.tags))) {
    throw new Error('Article updates were incomplete')
  }
  return payload
}

/** Refresh browser-restored pages without parallel requests or background polling. */
export function startArticleFreshness({ environment, load, onData, onError, onSettled }: {
  environment: ArticleFreshnessEnvironment
  load: (signal: AbortSignal) => Promise<AiArticlesResponse>
  onData: (payload: AiArticlesResponse) => void
  onError: (cause: unknown) => void
  onSettled: () => void
}): { refresh: () => void; dispose: () => void } {
  let disposed = false
  let active: AbortController | null = null
  let lastAttemptAt = Number.NEGATIVE_INFINITY

  const refresh = () => {
    if (disposed || active || !environment.isVisible()
      || environment.now() - lastAttemptAt < ARTICLE_MIN_REFRESH_INTERVAL_MS) return
    lastAttemptAt = environment.now()
    const controller = new AbortController()
    active = controller
    const aborted = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(new DOMException('Article refresh cancelled', 'AbortError')), { once: true })
    })
    const timeout = environment.setTimeout(() => controller.abort(), ARTICLE_REQUEST_TIMEOUT_MS)
    // Racing cancellation also bounds a stalled response body or a transport
    // that fails to settle promptly after its signal is aborted.
    void Promise.race([Promise.resolve().then(() => load(controller.signal)), aborted])
      .then(payload => { if (!disposed && !controller.signal.aborted) onData(payload) })
      .catch(cause => { if (!disposed) onError(cause) })
      .finally(() => {
        environment.clearTimeout(timeout)
        if (active === controller) active = null
        if (!disposed) onSettled()
      })
  }

  environment.windowEvents.addEventListener('focus', refresh)
  environment.windowEvents.addEventListener('pageshow', refresh)
  environment.windowEvents.addEventListener('online', refresh)
  environment.documentEvents.addEventListener('visibilitychange', refresh)
  const interval = environment.setInterval(refresh, ARTICLE_POLL_INTERVAL_MS)
  refresh()

  return {
    refresh,
    dispose: () => {
      disposed = true
      active?.abort()
      environment.clearInterval(interval)
      environment.windowEvents.removeEventListener('focus', refresh)
      environment.windowEvents.removeEventListener('pageshow', refresh)
      environment.windowEvents.removeEventListener('online', refresh)
      environment.documentEvents.removeEventListener('visibilitychange', refresh)
    },
  }
}
